import { styled } from 'goober';
import { forwardRef } from 'react';
import { FlexCol, FlexRow, FlexRowCentered } from '../layout/Flex';
import { keyframes } from 'goober';

type LayoutMode = 'modal' | 'fullscreen';

const modalAppear = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95) translateY(10px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
`;

const modalDisappear = keyframes`
    from {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
    to {
        opacity: 0;
        transform: scale(0.95) translateY(10px);
    }
`;

const fullscreenAppear = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const fullscreenDisappear = keyframes`
    from {
        opacity: 1;
    }
    to {
        opacity: 0;
    }
`;

const dotPulse = keyframes`
    0%, 80%, 100% {
        opacity: 0;
    }
    40% {
        opacity: 1;
    }
`;

export const ChatContentContainer = styled(
    FlexCol,
    forwardRef
)<{ $layoutMode: LayoutMode }>(({ theme }) => ({
    backgroundColor: theme.colors.ui.white,
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
}));

export const ChatBodyContainer = styled(
    FlexCol,
    forwardRef
)<{ $layoutMode: LayoutMode }>({
    flex: 1,
    minHeight: 0,
    position: 'relative',
});

export const MessageScrollContainer = styled(
    FlexCol,
    forwardRef
)<{ $layoutMode: LayoutMode }>({
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
});

export const MessageWrapper = styled(
    FlexCol,
    forwardRef
)<{ $layoutMode: LayoutMode }>(({ $layoutMode }) => ({
    padding: '0 10px',
    ...($layoutMode === 'fullscreen' && {
        maxWidth: '768px',
        margin: '0 auto',
        width: '100%',
    }),
}));

export const MessageContainer = styled(
    FlexRow,
    forwardRef
)<{ alignment: 'start' | 'end' }>(({ alignment }) => ({
    justifyContent: alignment === 'start' ? 'flex-start' : 'flex-end',
    alignItems: 'center',
}));

export const MessageText = styled(
    'div',
    forwardRef
)(({ theme }) => ({
    maxWidth: '350px',
    backgroundColor: theme.colors.neutral.w100,
    padding: '12px',
    borderRadius: '15px',
    lineHeight: '18px',
    wordWrap: 'break-word',
    display: 'inline-block',
}));

export const ReplyContainer = styled(
    FlexRow,
    forwardRef
)({
    minWidth: 0,
    maxWidth: '100%',
});

export const ReplyIconWrapper = styled(
    'div',
    forwardRef
)({
    flexShrink: 0,
    paddingTop: '4px',
});

export const ReplyText = styled(
    'div',
    forwardRef
)(({ theme }) => ({
    '--borderColor-default': theme.colors.neutral.w200,
    '--bgColor-muted': theme.colors.neutral.w50,
    '--bgColor-default': theme.colors.ui.white,
    '--bgColor-neutral-muted': theme.colors.neutral.w200,
    '--fgColor-default': theme.colors.ui.dark,
    lineHeight: '20px',
    padding: '6px',
    fontSize: '12px',
    minHeight: 'unset',
    color: 'black',
    backgroundColor: 'white',
    minWidth: 0,
    overflow: 'hidden',
}));

export const ScrollableBlock = styled(
    'div',
    forwardRef
)({
    overflowX: 'auto',
    maxWidth: '100%',
    '> table, > pre': {
        marginBottom: 0,
    },
});

export const InputContainer = styled(
    FlexRow,
    forwardRef
)<{ $layoutMode?: LayoutMode }>(({ $layoutMode }) => ({
    height: '32px',
    flexShrink: 0,
    ...($layoutMode === 'fullscreen'
        ? {
              width: '100%',
              maxWidth: '768px',
              margin: '16px auto 10px auto',
              padding: '0 10px',
              boxSizing: 'border-box',
          }
        : {
              margin: '16px 10px 10px 10px',
          }),
}));

export const InputWrapper = styled(
    'div',
    forwardRef
)({
    flexGrow: 1,
    minWidth: 0,
});

export const ModalContainer = styled(
    FlexCol,
    forwardRef
)<{ $isClosing?: boolean }>(({ theme, $isClosing }) => ({
    position: 'fixed',
    bottom: '16px',
    right: '16px',
    width: '550px',
    height: '550px',
    backgroundColor: theme.colors.ui.white,
    boxShadow: `0px 4px 16px ${theme.boxShadow}`,
    border: `1px solid ${theme.colors.neutral.w200}`,
    borderRadius: '16px',
    zIndex: theme.zIndex.MODAL,
    overflow: 'hidden',
    animation: `${$isClosing ? modalDisappear : modalAppear} 0.2s ease-out forwards`,
}));

export const FullScreenContainer = styled(
    FlexCol,
    forwardRef
)<{ $isClosing?: boolean }>(({ theme, $isClosing }) => ({
    position: 'absolute',
    inset: 0,
    zIndex: theme.zIndex.SIDE_NAV_OVERLAY,
    backgroundColor: theme.colors.ui.white,
    overflow: 'hidden',
    animation: `${$isClosing ? fullscreenDisappear : fullscreenAppear} 0.2s ease-out forwards`,
}));

export const HeaderContainer = styled(
    FlexRow,
    forwardRef
)<{ $layoutMode: LayoutMode; $sideNavClosed?: boolean }>(({ $layoutMode, $sideNavClosed }) => ({
    flexShrink: 0,
    ...($layoutMode === 'fullscreen'
        ? {
              padding: '20px',
              paddingLeft: $sideNavClosed ? '55px' : '10px',
              transition: 'padding-left 300ms ease',
          }
        : {
              padding: '10px',
          }),
}));

export const GradientIcon = styled(
    FlexRowCentered,
    forwardRef
)(({ theme }) => ({
    borderRadius: '100%',
    height: '24px',
    width: '24px',
    backgroundColor: theme.colors.secondary.w50,
}));

export const HeaderIcon = styled(
    FlexRowCentered,
    forwardRef
)(({ theme }) => ({
    borderRadius: '8px',
    height: '32px',
    width: '32px',
    backgroundColor: theme.colors.secondary.w50,
}));

export const SuggestedMessagesContainer = styled(
    'div',
    forwardRef
)<{ $layoutMode?: LayoutMode }>(({ $layoutMode }) => ({
    padding: '0px 10px',
    textAlign: 'left',
    position: 'absolute',
    bottom: '62px',
    ...($layoutMode === 'fullscreen' && {
        position: 'relative',
        bottom: 'unset',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
    }),
}));

const shimmer = keyframes`
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
`;

export const GeneratingSuggestionsContainer = styled(
    FlexCol,
    forwardRef
)({
    gap: '8px',
    // Fixed width: the modal container is absolutely positioned and shrink-wraps,
    // so a percentage width here would collapse.
    width: '260px',
});

export const GeneratingSuggestionsLabel = styled(
    'p',
    forwardRef
)(({ theme }) => ({
    color: theme.colors.neutral.w700,
    fontSize: '13px',
    margin: 0,
    paddingBottom: '4px',
}));

export const SkeletonChip = styled(
    'div',
    forwardRef
)<{ $width?: string }>(({ theme, $width }) => ({
    // Match the small Button height so the skeleton → chips swap doesn't shift layout.
    height: '24px',
    width: $width ?? '100%',
    borderRadius: '8px',
    background: `linear-gradient(90deg, ${theme.colors.neutral.w100} 25%, ${theme.colors.neutral.w50} 50%, ${theme.colors.neutral.w100} 75%)`,
    backgroundSize: '200% 100%',
    animation: `${shimmer} 1.3s ease-in-out infinite`,
}));

export const StreamingStatusContainer = styled(
    FlexRow,
    forwardRef
)(({ theme }) => ({
    color: theme.colors.neutral.w500,
    fontStyle: 'italic',
    minWidth: 0,
    maxWidth: '100%',
}));

export const StreamingStatusText = styled(
    'span',
    forwardRef
)({
    fontSize: '12px',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

export const StreamingDots = styled(
    'span',
    forwardRef
)({
    display: 'inline-flex',
    gap: '1px',
    span: {
        animation: `${dotPulse} 1.4s infinite ease-in-out`,
        opacity: 0,
        '&:nth-child(1)': {
            animationDelay: '0s',
        },
        '&:nth-child(2)': {
            animationDelay: '0.2s',
        },
        '&:nth-child(3)': {
            animationDelay: '0.4s',
        },
    },
});

export const ChatPreviewImage = styled('img')({
    maxWidth: '100%',
    maxHeight: '320px',
    borderRadius: '4px',
});
